import { useState } from "react";
import axios from "axios";
import { PaceChips } from "@/components/nobo/PaceChips";
import { WelcomeNote } from "@/components/nobo/WelcomeNote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 200;
const empty = { name: "", contact: "", why: "", pace: "", hpx: "" };

const validate = ({ name, contact, pace }) => {
  if (!name.trim()) return "Add your name so we know who to look for.";
  if (contact.trim().length < 3) return "Drop your Instagram handle or email so we can reach you.";
  if (!pace) return "Pick a pace. You can change it later.";
  return "";
};

export const WaitlistForm = ({ igUrl }) => {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const [err, setErr] = useState("");
  const [doneName, setDoneName] = useState("");
  const [already, setAlready] = useState(false);
  const [welcome, setWelcome] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value.slice(0, MAX) }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    const msg = validate(form);
    setErr(msg);
    if (msg) return;
    setStatus("sending");
    const name = form.name.trim();
    try {
      if (!form.hpx) {
        const { hpx, ...entry } = form;
        const { data } = await axios.post(`${API}/waitlist`, entry);
        setAlready(Boolean(data.already));
        setWelcome(data.id && data.welcome_token ? { id: data.id, token: data.welcome_token } : null);
      }
      setDoneName(name);
      setStatus("done");
    } catch (e) {
      setStatus("idle");
      setErr(e.response?.status === 429
        ? "Couldn't save your spot: too many tries from this connection. Wait a few minutes and try again."
        : "Couldn't save your spot. Check your connection and try again.");
    }
  };

  if (status === "done") {
    return (
      <div className="card done" data-testid="waitlist-success">
        <h2 data-testid="success-title">{already ? "you're already on the list." : "you're on the waitlist."}</h2>
        {welcome && <WelcomeNote id={welcome.id} token={welcome.token} />}
        <p className="sub">
          {already && "We've already got your request, so no need to send another. "}
          Being on the list isn't a confirmed spot yet. If we pick you for a run, we'll message you to confirm.
          Keep an eye on your DMs or inbox, and follow{" "}
          <a className="ig" href={igUrl} target="_blank" rel="noopener noreferrer" data-testid="success-instagram-link">
            @noborunclub
          </a>{" "}
          so you don't miss us.
        </p>
        <div className="tag" data-testid="success-tag">fingers crossed, {doneName.toLowerCase()}</div>
      </div>
    );
  }

  const sending = status === "sending";
  return (
    <form className="card" onSubmit={submit} noValidate data-testid="waitlist-form">
      <h2>request a spot</h2>
      <p className="sub">We keep each run small. Join the waitlist and if we pick you, we'll confirm by message.</p>
      <label htmlFor="name">first name</label>
      <input id="name" type="text" autoComplete="given-name" placeholder="what do we call you"
        maxLength={MAX} value={form.name} onChange={set("name")} data-testid="input-name" />
      <label htmlFor="ig">instagram or email</label>
      <input id="ig" type="text" autoComplete="off" placeholder="@you or you@mail.com"
        maxLength={MAX} value={form.contact} onChange={set("contact")} data-testid="input-contact" />
      <label htmlFor="why">why NOBO? (optional)</label>
      <input id="why" type="text" placeholder="one line is plenty"
        maxLength={MAX} value={form.why} onChange={set("why")} data-testid="input-why" />
      <PaceChips value={form.pace} onChange={set("pace")} />
      <input type="text" name="hpx_trap" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hp"
        value={form.hpx} onChange={set("hpx")} />
      <div className="err" role="alert" data-testid="form-error">{err}</div>
      <button className="go" type="submit" disabled={sending} data-testid="submit-button">
        {sending ? "adding you..." : err && err.startsWith("Couldn't") ? "try again" : "join the waitlist"}
      </button>
    </form>
  );
};
